import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

export const billingKeys = {
  plans: () => ["billing", "plans"] as const,
  subscriptions: () => ["billing", "subscriptions"] as const,
  companySubscription: (companyId: number) =>
    ["billing", "subscription", "company", companyId] as const,
};

export interface TPlan {
  id: number;
  slug: string;
  name: string;
  tier: string;
  price_monthly?: number | string;
  price_annual?: number | string;
  max_vehicles?: number | null;
  is_active?: number | boolean;
}

export interface TForcePaymentRequest {
  company_id: number;
  /**
   * SaaS plan id — or an AI add-on plan id alone, in which case the API
   * attaches the add-on to the company's existing active SaaS subscription.
   */
  plan_id: number;
  /** Optional AI add-on to activate alongside the SaaS plan. */
  ai_addon_id?: number | null;
  billing_cycle: "monthly" | "annual";
  vehicle_quantity?: number;
  duration_months?: number;
  /**
   * Duree en JOURS. Remplace `duration_months`, qui reste accepte par l'API
   * pour les appelants non migres.
   *
   * Le mois etait le seul grain disponible : prolonger un essai d'une semaine
   * ou couvrir dix jours entre deux virements obligeait a arrondir a un mois
   * entier, donc a donner plus que prevu.
   */
  duration_days?: number;
  note?: string;
}

export interface TAdminSubscription {
  id: number;
  company_id: number;
  plan_id: number;
  plan?: TPlan | null;
  billing_cycle: "monthly" | "annual";
  vehicle_quantity: number;
  status: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  ends_at?: string | null;
  metadata?: Record<string, any> | null;
}

/** Current (latest) subscription of one company (super-admin). */
export const useGetCompanySubscription = (
  companyId: number,
  enabled = true
) => {
  const { data, ...res } = useQuery({
    queryKey: billingKeys.companySubscription(companyId),
    enabled: enabled && companyId > 0,
    queryFn: async (): Promise<TAdminSubscription | null> => {
      const { data } = await axios.get(`/admin/billing/subscriptions`, {
        params: { company_id: companyId, per_page: 1 },
      });
      const list: TAdminSubscription[] = data?.data ?? [];
      return list[0] ?? null;
    },
  });
  return { subscription: data ?? null, ...res };
};

/** All plans, including inactive ones (super-admin). */
export const useGetAdminPlans = (enabled = true) => {
  const { data, ...res } = useQuery({
    queryKey: billingKeys.plans(),
    queryFn: async (): Promise<TPlan[]> => {
      const { data } = await axios.get(`/admin/billing/plans`);
      return data?.data ?? data?.plans ?? data ?? [];
    },
    enabled,
  });
  return { plans: data ?? [], ...res };
};

/**
 * Force payment (super-admin): manually activate a paid subscription for a
 * company without Stripe checkout (offline payment - bank transfer, cheque,
 * commercial gesture). The backend records a full audit trail in metadata.
 */
export const useForcePayment = (onSuccessCallback?: () => void) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TForcePaymentRequest) => {
      const { data } = await axios.post(
        `/admin/billing/subscriptions/force`,
        payload
      );
      return data?.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.subscriptions() });
      queryClient.invalidateQueries({
        queryKey: billingKeys.companySubscription(variables.company_id),
      });
      toast.success(
        t(
          "billing.forcePaymentSuccess",
          "Subscription activated successfully"
        )
      );
      onSuccessCallback?.();
    },
    onError: (err: any) => {
      toast.error(
        err?.response?.data?.message ??
          t("billing.forcePaymentError", "Failed to activate subscription")
      );
    },
  });
};
